'use client';

import { c as _c } from "react/compiler-runtime";
import clsx from 'clsx';
import { Link as RACLink } from 'react-aria-components';
import stylesAsButton from '~/components/Button/src/Button.module.css';
import { isMigratedPathname } from '~/i18n/i18n-config';
import { Link as IntlLink } from '~/i18n/navigation';
import styles from './Link.module.css';
import type { LinkProps } from './Link.types';
const isBywayInternalUrl = (href: LinkProps['href']) => {
  if (!href) return false;
  if (typeof href !== 'string') return false;
  if (href.startsWith('/')) return true;
  try {
    const url = new URL(href);
    return url.hostname.endsWith('byway.travel');
  } catch {
    return false;
  }
};
const resolveTarget = (href: LinkProps['href'], target: LinkProps['target'], explicitTarget: boolean): LinkProps['target'] => {
  if (target !== '_blank') {
    return target;
  }
  if (explicitTarget) {
    return target;
  }
  if (isBywayInternalUrl(href)) {
    return undefined;
  }
  return target;
};
const Link = t0 => {
  const $ = _c(58);
  let asButton;
  let children;
  let dataTestId;
  let hasUnderline;
  let href;
  let isDisabled;
  let isExternal;
  let isFullWidth;
  let isInline;
  let isPill;
  let isResponsive;
  let isTransparent;
  let isUnstyled;
  let noWrap;
  let onClick;
  let prefetch;
  let props;
  let size;
  let target;
  if ($[0] !== t0) {
    ({
      asButton,
      isPill,
      isTransparent,
      children,
      dataTestId,
      hasUnderline,
      href,
      isDisabled,
      isExternal,
      isFullWidth,
      isInline,
      isResponsive,
      isUnstyled,
      noWrap,
      onClick,
      prefetch,
      size,
      target,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asButton;
    $[2] = children;
    $[3] = dataTestId;
    $[4] = hasUnderline;
    $[5] = href;
    $[6] = isDisabled;
    $[7] = isExternal;
    $[8] = isFullWidth;
    $[9] = isInline;
    $[10] = isPill;
    $[11] = isResponsive;
    $[12] = isTransparent;
    $[13] = isUnstyled;
    $[14] = noWrap;
    $[15] = onClick;
    $[16] = prefetch;
    $[17] = props;
    $[18] = size;
    $[19] = target;
  } else {
    asButton = $[1];
    children = $[2];
    dataTestId = $[3];
    hasUnderline = $[4];
    href = $[5];
    isDisabled = $[6];
    isExternal = $[7];
    isFullWidth = $[8];
    isInline = $[9];
    isPill = $[10];
    isResponsive = $[11];
    isTransparent = $[12];
    isUnstyled = $[13];
    noWrap = $[14];
    onClick = $[15];
    prefetch = $[16];
    props = $[17];
    size = $[18];
    target = $[19];
  }
  const defaultTarget = target ?? (isBywayInternalUrl(href) ? undefined : "_blank");
  const t1 = target !== undefined;
  let t2;
  if ($[20] !== defaultTarget || $[21] !== href || $[22] !== t1) {
    t2 = resolveTarget(href, defaultTarget, t1);
    $[20] = defaultTarget;
    $[21] = href;
    $[22] = t1;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  const resolvedTarget = t2;
  const rel = resolvedTarget === "_blank" ? "noreferrer noopener" : undefined;
  let t3;
  if ($[24] !== href) {
    t3 = typeof href === "string" && href.startsWith("/") && !isMigratedPathname(href.split(/[?#]/)[0]);
    $[24] = href;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  const isInternalNonMigratedHref = t3;
  const isNativeAnchor = isDisabled || isInternalNonMigratedHref;
  const InternalLink = isNativeAnchor ? "a" : IntlLink;
  let t4;
  if ($[26] !== asButton || $[27] !== hasUnderline || $[28] !== isDisabled || $[29] !== isExternal || $[30] !== isFullWidth || $[31] !== isInline || $[32] !== isPill || $[33] !== isResponsive || $[34] !== isTransparent || $[35] !== isUnstyled || $[36] !== noWrap || $[37] !== size) {
    t4 = isUnstyled ? undefined : clsx(styles.link, {
      [styles.inline]: isInline,
      [styles.underline]: hasUnderline,
      [styles.external]: isExternal,
      [styles.disabled]: isDisabled,
      [stylesAsButton.button]: asButton,
      [stylesAsButton.primary]: asButton === "primary",
      [stylesAsButton.secondary]: asButton === "secondary",
      [stylesAsButton.tertiary]: asButton === "tertiary",
      [stylesAsButton.neutral]: asButton === "neutral",
      [stylesAsButton.pill]: isPill,
      [stylesAsButton.transparent]: isTransparent,
      [stylesAsButton.icon]: asButton === "icon",
      [stylesAsButton.small]: size === "small",
      [stylesAsButton.extraSmall]: size === "extraSmall",
      [stylesAsButton.fullWidth]: size === "fullWidth" || isFullWidth,
      [stylesAsButton.responsive]: size === "responsive" || isResponsive,
      [stylesAsButton.large]: size === "large",
      [stylesAsButton.noWrap]: noWrap
    });
    $[26] = asButton;
    $[27] = hasUnderline;
    $[28] = isDisabled;
    $[29] = isExternal;
    $[30] = isFullWidth;
    $[31] = isInline;
    $[32] = isPill;
    $[33] = isResponsive;
    $[34] = isTransparent;
    $[35] = isUnstyled;
    $[36] = noWrap;
    $[37] = size;
    $[38] = t4;
  } else {
    t4 = $[38];
  }
  const linkStyles = t4;
  let t5;
  if ($[39] !== isDisabled || $[40] !== onClick) {
    t5 = e => {
      if (isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    };
    $[39] = isDisabled;
    $[40] = onClick;
    $[41] = t5;
  } else {
    t5 = $[41];
  }
  const handleClick = t5;
  let t6;
  if ($[42] !== InternalLink || $[43] !== children || $[44] !== dataTestId || $[45] !== handleClick || $[46] !== href || $[47] !== isDisabled || $[48] !== isExternal || $[49] !== isNativeAnchor || $[50] !== isUnstyled || $[51] !== linkStyles || $[52] !== onClick || $[53] !== prefetch || $[54] !== props || $[55] !== rel || $[56] !== resolvedTarget) {
    t6 = <>{isExternal ? <RACLink {...props} data-test-id={dataTestId ?? "Link-external"} href={isDisabled ? undefined : href} target={resolvedTarget} rel={rel} className={linkStyles} isDisabled={isDisabled} onClick={e_0 => {
        if (!isDisabled) {
          onClick?.(e_0);
        }
      }}>{children}</RACLink> : <InternalLink {...props} data-test-id={dataTestId ?? "Link-internal"} href={isDisabled ? undefined : href} {...isNativeAnchor ? {} : {
        prefetch
      }} target={resolvedTarget} rel={rel} className={linkStyles} onClick={handleClick} aria-disabled={isDisabled} data-disabled={isDisabled || undefined}>{isUnstyled ? children : <span className={styles.span}>{children}</span>}</InternalLink>}</>;
    $[42] = InternalLink;
    $[43] = children;
    $[44] = dataTestId;
    $[45] = handleClick;
    $[46] = href;
    $[47] = isDisabled;
    $[48] = isExternal;
    $[49] = isNativeAnchor;
    $[50] = isUnstyled;
    $[51] = linkStyles;
    $[52] = onClick;
    $[53] = prefetch;
    $[54] = props;
    $[55] = rel;
    $[56] = resolvedTarget;
    $[57] = t6;
  } else {
    t6 = $[57];
  }
  return t6;
};
export { Link };